import React, { useState, useEffect } from 'react';
import { Navbar } from '../components/Navbar';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { Button } from '../components/ui/button';
import { Users, FileText, MessageSquare, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminContent = () => {
  const [stats, setStats] = useState(null);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchStats();
      fetchResources();
    }
  }, [user]);

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(data);
    } catch (error) {
      toast.error('Failed to load stats');
    }
  };

  const fetchResources = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/resources`, { withCredentials: true });
      setResources(data);
    } catch (error) {
      toast.error('Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (resourceId) => {
    if (!window.confirm('Are you sure you want to delete this resource?')) return;

    try {
      await axios.delete(`${API}/admin/resources/${resourceId}`, { withCredentials: true });
      toast.success('Resource deleted successfully');
      fetchResources();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete resource');
    }
  };

  if (!user || user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-semibold tracking-tight mb-2" data-testid="admin-title">Admin Panel</h1>
          <p className="text-foreground/70">Manage platform resources and users</p>
        </div>

        {stats && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" data-testid="admin-stats">
            <div className="p-6 rounded-lg border bg-surface">
              <div className="flex items-center gap-3 mb-2">
                <Users className="h-5 w-5 text-primary" strokeWidth={1.5} />
                <span className="text-sm text-foreground/70">Total Users</span>
              </div>
              <p className="text-3xl font-semibold" data-testid="stat-users">{stats.total_users}</p>
            </div>

            <div className="p-6 rounded-lg border bg-surface">
              <div className="flex items-center gap-3 mb-2">
                <FileText className="h-5 w-5 text-primary" strokeWidth={1.5} />
                <span className="text-sm text-foreground/70">Total Resources</span>
              </div>
              <p className="text-3xl font-semibold" data-testid="stat-resources">{stats.total_resources}</p>
            </div>

            <div className="p-6 rounded-lg border bg-surface">
              <div className="flex items-center gap-3 mb-2">
                <MessageSquare className="h-5 w-5 text-primary" strokeWidth={1.5} />
                <span className="text-sm text-foreground/70">Total Comments</span>
              </div>
              <p className="text-3xl font-semibold" data-testid="stat-comments">{stats.total_comments}</p>
            </div>

            <div className="p-6 rounded-lg border bg-surface">
              <div className="flex items-center gap-3 mb-2">
                <FileText className="h-5 w-5 text-orange-500" strokeWidth={1.5} />
                <span className="text-sm text-foreground/70">Pending</span>
              </div>
              <p className="text-3xl font-semibold" data-testid="stat-pending">{stats.pending_resources}</p>
            </div>
          </div>
        )}

        <div>
          <h2 className="text-2xl font-semibold tracking-tight mb-6">All Resources</h2>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : resources.length === 0 ? (
            <div className="text-center py-20 border rounded-lg" data-testid="admin-empty-state">
              <p className="text-foreground/70">No resources found</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden" data-testid="admin-resources-table">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-surface border-b">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-foreground/80">Title</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-foreground/80">Subject</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-foreground/80">Uploader</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-foreground/80">Created</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-foreground/80">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {resources.map((resource) => (
                      <tr key={resource.id} className="hover:bg-surface/50" data-testid={`admin-resource-row-${resource.id}`}>
                        <td className="px-6 py-4">
                          <p className="font-medium">{resource.title}</p>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-xs uppercase tracking-wider font-mono text-foreground/75">
                            {resource.subject}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <p className="text-sm font-medium">{resource.uploader_name}</p>
                            <p className="text-xs text-foreground/65">{resource.uploader_email}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-foreground/70">
                          {formatDistanceToNow(new Date(resource.created_at), { addSuffix: true })}
                        </td>
                        <td className="px-6 py-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(resource.id)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
                            data-testid={`admin-delete-${resource.id}`}
                          >
                            <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const Admin = () => (
  <ProtectedRoute>
    <AdminContent />
  </ProtectedRoute>
);